import { useEffect, useState } from 'react';
import { TokenMetadata, ftGetTokenMetadata } from './ft-contract';
import { wallet } from './near';
import getConfig from './config';
import { listClmmPools } from './clmmPool';

const PAGE_SIZE = 100;

type CoinStatus =
  | string
  | {
      graduated?: { pool_id: string };
      graduated_v4?: { pool_id: string };
    };

type ZearfunCoin = {
  token_id: string;
  status: CoinStatus;
};

export function isGraduatedStatus(status: CoinStatus): boolean {
  if (typeof status === 'string') {
    return status === 'graduated' || status === 'graduated_v4';
  }
  if (status && typeof status === 'object') {
    return 'graduated' in status || 'graduated_v4' in status;
  }
  return false;
}

export async function getGraduatedZearfunTokenIds(): Promise<string[]> {
  const contractId = getConfig().ZEARFUN_CONTRACT_ID;
  if (!contractId) return [];

  const ids: string[] = [];
  for (let page = 0; page < 50; page++) {
    const coins: ZearfunCoin[] = await wallet
      .account()
      .viewFunction(contractId, 'list_coins', {
        from_index: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
    if (!coins?.length) break;
    coins.forEach((coin) => {
      if (coin?.token_id && isGraduatedStatus(coin.status)) {
        ids.push(coin.token_id);
      }
    });
    if (coins.length < PAGE_SIZE) break;
  }
  return ids;
}

// Tokens that belong to this DEX: exchange whitelist, classic pools,
// CLMM pools, and coins graduated from the launchpad.
export async function getZearDexTokenIds(): Promise<string[]> {
  const config = getConfig();
  const ids = new Set<string>(await getGraduatedZearfunTokenIds());
  const exchange = config.REF_FI_CONTRACT_ID;
  if (exchange) {
    const account = wallet.account();
    const whitelist: string[] = await account
      .viewFunction(exchange, 'get_whitelisted_tokens', {})
      .catch(() => []);
    whitelist.forEach((id) => id && ids.add(id));

    const count = Number(
      await account
        .viewFunction(exchange, 'get_number_of_pools', {})
        .catch(() => 0)
    );
    for (let from = 0; from < count; from += 200) {
      const pools: { token_account_ids?: string[] }[] = await account
        .viewFunction(exchange, 'get_pools', { from_index: from, limit: 200 })
        .catch(() => []);
      if (!pools?.length) break;
      pools.forEach((pool) => {
        (pool.token_account_ids || []).forEach((id) => id && ids.add(id));
      });
    }
  }

  const clmmPools = await listClmmPools().catch(() => []);
  clmmPools.forEach((pool) => {
    [pool.currency0, pool.currency1].forEach((id) => {
      if (id && id !== 'near') ids.add(id);
    });
  });
  return [...ids];
}

export function useZearDexTokens(): TokenMetadata[] {
  const [tokens, setTokens] = useState<TokenMetadata[]>([]);

  useEffect(() => {
    let cancelled = false;
    getZearDexTokenIds()
      .then((ids) =>
        Promise.all(
          ids.map((id) =>
            ftGetTokenMetadata(id).catch(() => null as TokenMetadata | null)
          )
        )
      )
      .then((metadata) => {
        if (cancelled) return;
        setTokens(
          metadata
            .filter((token): token is TokenMetadata => !!token?.id)
            .map((token) => ({ ...token, onRef: true }))
        );
      })
      .catch(() => {
        if (!cancelled) setTokens([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return tokens;
}

export function useGraduatedZearfunTokens(): TokenMetadata[] {
  const [tokens, setTokens] = useState<TokenMetadata[]>([]);

  useEffect(() => {
    let cancelled = false;
    getGraduatedZearfunTokenIds()
      .then((ids) =>
        Promise.all(
          ids.map((id) =>
            ftGetTokenMetadata(id).catch(() => null as TokenMetadata | null)
          )
        )
      )
      .then((metadata) => {
        if (cancelled) return;
        setTokens(
          metadata
            .filter((token): token is TokenMetadata => !!token?.id)
            .map((token) => ({ ...token, onRef: true }))
        );
      })
      .catch(() => {
        if (!cancelled) setTokens([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return tokens;
}
